import React, { useEffect, useMemo, useState } from 'react';
import { authorizedFetch } from '../utils/api';

function formatPublishTime(value) {
  if (!value) return 'Unknown publish time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown publish time';
  return date.toLocaleString();
}

function sourceBadgeClass(source) {
  const key = (source || '').toLowerCase();
  if (key.includes('vogue')) return 'bg-pink-100 text-pink-700';
  if (key.includes('business')) return 'bg-blue-100 text-blue-700';
  if (key.includes('wwd')) return 'bg-purple-100 text-purple-700';
  return 'bg-emerald-100 text-emerald-700';
}

export default function News() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState({ asOfUtc: '', cutoffUtc: '', count: 0 });

  const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await authorizedFetch(`${backendUrl}/news/top-daily?limit=15`);
        if (!response.ok) {
          throw new Error(`News request failed with status ${response.status}`);
        }
        const payload = await response.json();
        setItems(payload.items || []);
        setMeta({
          asOfUtc: payload.as_of_utc || '',
          cutoffUtc: payload.daily_cutoff_utc || '',
          count: payload.count || 0,
        });
      } catch (err) {
        setError(err.message || 'Failed to fetch fashion news');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [backendUrl]);

  const headline = useMemo(() => {
    if (loading) return 'Loading top fashion news...';
    if (error) return 'Unable to load fashion news';
    return `Top Daily Fashion News (${meta.count})`;
  }, [loading, error, meta.count]);

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-800">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{headline}</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          As of: {meta.asOfUtc ? formatPublishTime(meta.asOfUtc) : 'Unknown'} | Daily cutoff:{' '}
          {meta.cutoffUtc ? formatPublishTime(meta.cutoffUtc) : 'Unknown'}
        </p>
      </div>

      {loading && (
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-800 text-gray-600 dark:text-gray-300">
          Fetching the latest headlines from top fashion publications...
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl shadow-sm p-6 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl shadow-sm p-6 border border-yellow-200 dark:border-yellow-900 text-yellow-800 dark:text-yellow-300">
          No fashion news stories were available right now.
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((item, index) => (
            <a
              key={`${item.url}-${index}`}
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="block bg-white dark:bg-gray-900 rounded-xl shadow-sm p-5 border border-gray-100 dark:border-gray-800 hover:border-blue-300 hover:shadow-md transition"
            >
              <div className="flex items-center justify-between mb-3 gap-4">
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${sourceBadgeClass(item.source)}`}>
                  {item.source || 'Unknown source'}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatPublishTime(item.published_at)}
                </span>
              </div>
              <h2 className="text-lg font-semibold leading-snug text-gray-900 dark:text-gray-100 mb-2">
                {item.title}
              </h2>
              {item.summary && (
                <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3">
                  {item.summary}
                </p>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
